import {
  useSite,
  useSiteFileDownload,
  useWarm,
} from "@inrange/building-manager-api-client";
import {
  SiteFile,
  SiteOwnership,
} from "@inrange/building-manager-api-client/models-site";
import { reversed, sortBy, sortByAdditional } from "@inrange/utils";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import { Button, Card, Container, Spinner, Table } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";
import Loading from "../Loading";
import TransferReadingsValidation from "./billing/TransferReadingsValidation";
import InvoiceValidation from "./billing/InvoiceValidation";
import ReadingValidation from "./billing/ReadingValidation";
import EditFileModal from "./siteSections/EditFileModal";
import SiteBillingOperationalActions from "./siteSections/SiteBillingOperationalActions";
import {
  currencyValue,
  epochSecondsToDate,
  epochSecondsToDateInTz,
  epochSecondsToDateTime,
} from "./siteSections/utils";

const SiteFileRow = ({
  siteId,
  currencyCode,
  file,
  setSelectedFile,
  onDelete,
  onTogglePublish,
  hasBattery,
  isDeleting,
  isTogglingPublish,
  enableValidation,
  siteTimeZone,
}: {
  siteId: string;
  currencyCode: string;
  file: SiteFile;
  setSelectedFile: (file: SiteFile) => void;
  onDelete: (file: SiteFile) => void;
  onTogglePublish: (file: SiteFile) => void;
  hasBattery: boolean;
  isDeleting: boolean;
  isTogglingPublish: boolean;
  enableValidation: boolean;
  siteTimeZone?: string;
}) => {
  const { fetchSiteFileURL } = useSiteFileDownload({
    siteId,
    file,
    isAttachment: true,
    enabled: false,
  });
  const [validationIcon, setValidationIcon] = useState<string | null>(null);
  const onValidationIcon = useCallback(
    (icon: string | null) => setValidationIcon(icon),
    []
  );

  const onDownload = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Fetch a download link
    const response = await fetchSiteFileURL.refetch();
    if (!response.data) return;
    const newURL = response.data.url;

    // Create a download link and click it
    const link = document.createElement("a");
    link.href = newURL;
    link.setAttribute("download", file.fileName);
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  };

  // Extract month from reading file name (format: YYYY-MM-readings.xlsx)
  const readingMonth =
    file.fileType === "reading" &&
    /^\d{4}-\d{2}-readings\.xlsx$/.test(file.fileName)
      ? file.fileName.substring(0, 7)
      : null;

  // Extract month from export/import reading file name (format: YYYY-MM-{export|import}-readings[-suffix].xlsx)
  const transferReadingMonth =
    (file.fileType === "exportReading" || file.fileType === "importReading") &&
    /^\d{4}-\d{2}-(export|import)-readings(-\S+)?\.xlsx$/.test(file.fileName)
      ? file.fileName.substring(0, 7)
      : null;

  // Extract month from invoice description (format: "Jan 2026 Invoice")
  const invoiceMonth = useMemo(() => {
    if (file.fileType !== "invoice" || !file.description) return null;
    const match = file.description.match(
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\s+Invoice$/
    );
    if (!match) return null;
    const monthNames: Record<string, string> = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };
    return `${match[2]}-${monthNames[match[1]]}`;
  }, [file.fileType, file.description]);

  return (
    <tr style={{ fontStyle: file.isPublished ? "normal" : "italic" }}>
      <td>
        {validationIcon && <>{validationIcon} </>}
        {file.fileName}
        {!file.isPublished ? " (not published)" : ""}
        {file.fileType === "invoice" &&
          file.invoiceOtherCharges &&
          file.invoiceOtherCharges.length > 0 && (
            <div
              style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}
            >
              {file.invoiceOtherCharges.map((item, i) => (
                <div key={i}>
                  {item.type === "consumption_adjustment" ? "[adj] " : ""}
                  {item.description}:{" "}
                  {item.type === "consumption_adjustment"
                    ? `${item.amount} kWh`
                    : `${item.amount < 0 ? "-" : ""}${currencyValue(Math.abs(item.amount), currencyCode)}`}
                </div>
              ))}
            </div>
          )}
        {enableValidation && readingMonth && (
          <ReadingValidation
            siteId={siteId}
            fileGuid={file.fileGuid}
            month={readingMonth}
            hasBattery={hasBattery}
            siteTimeZone={siteTimeZone}
            onIcon={onValidationIcon}
          />
        )}
        {enableValidation &&
          invoiceMonth &&
          file.fileName.toLowerCase().endsWith(".pdf") && (
            <InvoiceValidation
              siteId={siteId}
              fileGuid={file.fileGuid}
              month={invoiceMonth}
              onIcon={onValidationIcon}
            />
          )}
        {enableValidation && transferReadingMonth && (file.fileType === "importReading" ? file.buyerEsysUuid : file.sellerEsysUuid) && (
          <TransferReadingsValidation
            siteId={siteId}
            month={transferReadingMonth}
            esysUuid={(file.fileType === "importReading" ? file.buyerEsysUuid : file.sellerEsysUuid)!}
            isImport={file.fileType === "importReading"}
            counterpartyEsysUuid={(file.fileType === "importReading" ? file.sellerEsysUuid : file.buyerEsysUuid)!}
            currencyCode={currencyCode}
          />
        )}
      </td>
      <td>{file.description || ""}</td>
      {file.fileType === "reading" && (
        <>
          <td></td>
          <td></td>
          <td>{epochSecondsToDate(file.date)}</td>
          <td></td>
        </>
      )}
      {file.fileType === "statement" && (
        <>
          <td>{file.statementNumber || ""}</td>
          <td>
            {file.statementAmount
              ? currencyValue(file.statementAmount, currencyCode)
              : ""}
          </td>
          <td>{epochSecondsToDateInTz(file.issueDate, siteTimeZone)}</td>
          <td></td>
        </>
      )}
      {file.fileType === "invoice" && (
        <>
          <td>{file.invoiceNumber || ""}</td>
          <td>
            {file.invoiceAmount
              ? currencyValue(file.invoiceAmount, currencyCode)
              : ""}
          </td>
          <td>{epochSecondsToDateInTz(file.issueDate, siteTimeZone)}</td>
          <td>{epochSecondsToDateInTz(file.invoiceDueDate, siteTimeZone)}</td>
        </>
      )}
      {file.fileType === "siteDocument" && (
        <>
          <td></td>
          <td></td>
          <td>{epochSecondsToDate(file.date)}</td>
          <td></td>
        </>
      )}
      {(file.fileType === "exportReading" ||
        file.fileType === "importReading") && (
        <>
          <td></td>
          <td></td>
          <td>{epochSecondsToDate(file.date)}</td>
          <td></td>
        </>
      )}
      <td style={{ fontStyle: "normal", fontSize: "0.85em" }}>
        <div
          style={{
            color: file.createdAt !== file.updatedAt ? "#666" : "inherit",
          }}
        >
          <div>
            {file.createdAt !== file.updatedAt && `Created: `} {file.createdBy}
          </div>
          <div>{epochSecondsToDateTime(file.createdAt)}</div>
        </div>

        {file.createdAt !== file.updatedAt && (
          <div>
            <div>Updated: {file.updatedBy}</div>
            <div>{epochSecondsToDateTime(file.updatedAt)}</div>
          </div>
        )}
      </td>
      <td style={{ fontStyle: "normal" }}>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant={file.isPublished ? "outline-secondary" : "success"}
            onClick={() => onTogglePublish(file)}
            disabled={isTogglingPublish}
          >
            {isTogglingPublish ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Saving...
              </>
            ) : file.isPublished ? (
              "Unpublish"
            ) : (
              "Publish"
            )}
          </Button>
          <Button
            variant="primary"
            onClick={onDownload}
            data-testid={`${file.fileName}-download`}
          >
            Download
          </Button>
          <Button variant="primary" onClick={() => setSelectedFile(file)}>
            Edit
          </Button>
          <Button
            variant="danger"
            onClick={() => onDelete(file)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const FileTypeFilter = ({
  selectedFileTypes,
  setSelectedFileTypes,
  allFileTypes,
}: {
  selectedFileTypes: string[];
  setSelectedFileTypes: (types: string[]) => void;
  allFileTypes: string[];
}) => {
  const fileTypeLabels: Record<string, string> = {
    reading: "Reading",
    exportReading: "Export Reading",
    importReading: "Import Reading",
    invoice: "Invoice",
    statement: "Statement",
    siteDocument: "Site Document",
  };
  const options = allFileTypes.map((t) => ({
    label: fileTypeLabels[t] ?? t,
    value: t,
  }));
  return (
    <Select
      placeholder="Filter by file type..."
      value={options.filter((o) => selectedFileTypes.includes(o.value))}
      onChange={(values) => {
        setSelectedFileTypes(values ? values.map((v) => v.value) : []);
      }}
      options={options}
      components={{
        MultiValue: ({ index }) => {
          if (index > 0) return null;
          return (
            <div
              style={{
                marginLeft: "4px",
                color: "var(--bs-body-color)",
              }}
            >
              {selectedFileTypes.length}/{allFileTypes.length} types selected
            </div>
          );
        },
      }}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          border: "var(--bs-border-width) solid var(--bs-border-color)",
          borderRadius: "var(--bs-border-radius)",
          color: "var(--bs-body-color)",
          minWidth: "220px",
        }),
        menuList: (baseStyles) => ({
          ...baseStyles,
          maxHeight: "400px",
        }),
        option: (baseStyles, state) => ({
          ...baseStyles,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          "::before": {
            content: state.isSelected ? '"☑"' : '"☐"',
            fontSize: "1.2em",
          },
          backgroundColor: state.isFocused
            ? "var(--bs-primary-bg-subtle)"
            : "transparent",
          color: "var(--bs-body-color)",
        }),
      }}
      isMulti
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
    />
  );
};

const SiteFilesGroup = ({
  siteId,
  currencyCode,
  siteOwnership,
  siteFiles,
  setSelectedFile,
  onDelete,
  onTogglePublish,
  hasBattery,
  deletingFileGuid,
  togglingPublishFileGuid,
  groupLabel,
  siteTimeZone,
}: {
  siteId: string;
  currencyCode: string;
  siteOwnership: SiteOwnership | undefined;
  siteFiles: SiteFile[];
  setSelectedFile: (file: SiteFile) => void;
  onDelete: (file: SiteFile) => void;
  onTogglePublish: (file: SiteFile) => void;
  hasBattery: boolean;
  deletingFileGuid: string | null;
  togglingPublishFileGuid: string | null;
  siteTimeZone?: string;
  groupLabel?: string;
}) => {
  const siteFileRows = siteFiles
    .sort(
      sortByAdditional(
        (file) => -file.createdAt, // Negate to get newest-first with sortBy
        sortByAdditional(
          (file) => (file.fileType === "reading" ? 1 : 0), // Readings below other types
          reversed(sortBy((file) => file.date || file.issueDate || 0))
        )
      )
    )
    .map((file, index) => (
      <SiteFileRow
        key={file.fileGuid}
        siteId={siteId}
        currencyCode={currencyCode}
        file={file}
        setSelectedFile={setSelectedFile}
        onDelete={onDelete}
        onTogglePublish={onTogglePublish}
        hasBattery={hasBattery}
        isDeleting={deletingFileGuid === file.fileGuid}
        isTogglingPublish={togglingPublishFileGuid === file.fileGuid}
        enableValidation={import.meta.env.PROD || index < 4}
        siteTimeZone={siteTimeZone}
      />
    ));

  return (
    <Card body className="mt-2">
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <strong>
            {groupLabel
              ? groupLabel
              : siteOwnership
                ? `${siteOwnership.name} ${siteOwnership.ownership.charAt(0).toUpperCase() + siteOwnership.ownership.slice(1)}`
                : "Common"}{" "}
            files ({siteFiles.length})
          </strong>
        </div>
        <Button
          variant="primary"
          onClick={() =>
            setSelectedFile({
              siteID: siteId!,
              fileType: "reading",
              fileName: "",
              fileGuid: "",
              fileVersionId: null,
              siteOwnerships: siteOwnership ? [siteOwnership] : [],
              isPublished: true,
              // TODO: Fix these so they're not needed here, as the server overrides them
              createdBy: "",
              createdAt: 0,
              updatedAt: 0,
            })
          }
        >
          Add
        </Button>
      </div>
      {siteFileRows.length > 0 && (
        <Table bordered size="sm" responsive style={{ margin: "8px 0 0 0" }}>
          <thead>
            <tr>
              <th>File name</th>
              <th>Description</th>
              <th>Invoice/statement no.</th>
              <th>Amount</th>
              <th>Issue date</th>
              <th>Due date</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{siteFileRows}</tbody>
        </Table>
      )}
    </Card>
  );
};

const allFileTypes = ["reading", "exportReading", "importReading", "invoice", "statement", "siteDocument"];

const SiteFiles = () => {
  useWarm("sitefiles");
  const { siteId } = useParams<{ siteId: string }>();
  const { fetchSite, updateSite } = useSite({
    siteId,
    app: "admin",
  });
  const [selectedFile, setSelectedFile] = useState<SiteFile | undefined>(
    undefined
  );
  const [deletingFileGuid, setDeletingFileGuid] = useState<string | null>(null);
  const [togglingPublishFileGuid, setTogglingPublishFileGuid] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFileTypes = useMemo(() => {
    const typesParam = searchParams.get("fileTypes");
    if (typesParam === null) return allFileTypes;
    if (typesParam === "") return [];
    const types = typesParam.split(",");
    return types.filter((t) => allFileTypes.includes(t));
  }, [searchParams]);
  const setSelectedFileTypes = useCallback(
    (types: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (types.length === allFileTypes.length) {
          next.delete("fileTypes");
        } else {
          next.set("fileTypes", types.join(","));
        }
        return next;
      });
    },
    [setSearchParams]
  );
  const site = fetchSite.data?.site;

  if (!fetchSite.isLoading && fetchSite.isError) {
    return <div>Error loading the site.</div>;
  }

  if (fetchSite.isLoading || !fetchSite.data?.site) {
    return <Loading label="Loading site data..." />;
  }

  const siteFiles = (site?.siteFiles || []).filter((file) =>
    selectedFileTypes.includes(file.fileType)
  );

  const onDelete = (deletedFile: SiteFile) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${deletedFile.fileName}${deletedFile.isPublished ? " (published)" : " (not published)"}"?`
    );

    if (!confirmDelete) {
      return;
    }

    setDeletingFileGuid(deletedFile.fileGuid);
    updateSite.mutate(
      {
        site: {},
        deletedFiles: [deletedFile],
      },
      {
        onError: (error) => {
          alert(`Error deleting file: ${error}`);
          setDeletingFileGuid(null);
        },
        onSuccess: () => {
          setDeletingFileGuid(null);
        },
      }
    );
  };

  const onTogglePublish = (file: SiteFile) => {
    setTogglingPublishFileGuid(file.fileGuid);
    const updatedFile = { ...file, isPublished: !file.isPublished };
    updateSite.mutate(
      {
        site: {},
        addedFiles: [updatedFile],
      },
      {
        onError: (error) => {
          alert(`Error updating file: ${error}`);
          setTogglingPublishFileGuid(null);
        },
        onSuccess: () => {
          setTogglingPublishFileGuid(null);
        },
      }
    );
  };

  const siteOwnerships = site!.siteOwnerships.sort(sortBy((so) => so.name));
  const hasBattery = !!site!.totalBatterySummary;

  return (
    <Container fluid style={{ padding: "12px 12px 0px 12px" }}>
      <SiteBillingOperationalActions site={site!} />
      <div className="mt-2">
        <FileTypeFilter
          selectedFileTypes={selectedFileTypes}
          setSelectedFileTypes={setSelectedFileTypes}
          allFileTypes={allFileTypes}
        />
      </div>
      <fieldset disabled={fetchSite.data?.site.locked}>
          {siteOwnerships.map((so) => (
            <SiteFilesGroup
              key={so.orgID}
              siteId={siteId!}
              currencyCode={site!.currencyCode}
              siteOwnership={so}
              siteFiles={siteFiles.filter((file) =>
                file.siteOwnerships.some(
                  (ownership) => ownership.orgID === so.orgID
                )
              )}
              setSelectedFile={setSelectedFile}
              onDelete={onDelete}
              onTogglePublish={onTogglePublish}
              hasBattery={hasBattery}
              deletingFileGuid={deletingFileGuid}
              togglingPublishFileGuid={togglingPublishFileGuid}
              siteTimeZone={site!.timeZone}
            />
          ))}
          <SiteFilesGroup
            siteId={siteId!}
            siteOwnership={undefined}
            currencyCode={site!.currencyCode}
            siteFiles={siteFiles.filter(
              (file) => file.siteOwnerships.length === 0 && !file.isInternal
            )}
            setSelectedFile={setSelectedFile}
            onDelete={onDelete}
            onTogglePublish={onTogglePublish}
            hasBattery={hasBattery}
            deletingFileGuid={deletingFileGuid}
            togglingPublishFileGuid={togglingPublishFileGuid}
            siteTimeZone={site!.timeZone}
          />
          {siteFiles.some((file) => file.isInternal) && (
            <SiteFilesGroup
              siteId={siteId!}
              siteOwnership={undefined}
              currencyCode={site!.currencyCode}
              siteFiles={siteFiles.filter((file) => file.isInternal)}
              setSelectedFile={setSelectedFile}
              onDelete={onDelete}
              onTogglePublish={onTogglePublish}
              hasBattery={hasBattery}
              deletingFileGuid={deletingFileGuid}
              togglingPublishFileGuid={togglingPublishFileGuid}
              siteTimeZone={site!.timeZone}
              groupLabel="Internal"
            />
          )}
          {selectedFile && (
            <EditFileModal
              site={site!}
              siteFile={selectedFile}
              onClose={() => setSelectedFile(undefined)}
            />
          )}
      </fieldset>
    </Container>
  );
};

export default SiteFiles;
